// ---------------------------------------------------------------------------
// Apocalypse sound effects (Web Audio API) — per-app notification tones.
//
// Every banner during the glitch fires a sound that tries to *feel* like the
// real notification of the app it's from — the two-note WhatsApp bloop, the
// bell-like Calendar chime, the crisp iMessage note, etc. Combined, when
// they overlap during the storm, the effect is a phone that sounds like
// it's genuinely crashing: multiple apps competing for the speaker.
//
// Web Audio synthesis chosen over bundled MP3s because:
//   • no asset shipping / trademark risk (we approximate, we don't clone)
//   • offline-safe (no fetch, no permissions)
//   • pitch / length / harmonics stay tunable in code
//
// iOS Safari requires the AudioContext to be created from a user gesture,
// so `unlockApocalypseAudio()` is wired to the LockScreen's first
// pointerdown. After that single unlock, any number of pings can fire
// silently in the background — no further gesture needed.
// ---------------------------------------------------------------------------

let ctxSingleton = null;
let unlocked = false;

function ensureContext() {
  if (typeof window === "undefined") return null;
  if (ctxSingleton) return ctxSingleton;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctxSingleton = new Ctor();
  } catch {
    return null;
  }
  return ctxSingleton;
}

// ---------------------------------------------------------------------------
// Pre-decoded sample cache. HTML5 <audio> with cloneNode() has an ~30-100 ms
// latency on iOS Safari because .play() is async and the clone re-decodes
// on demand. AudioBufferSourceNode.start() is sample-accurate (<5 ms) once
// the buffer is decoded once. We fetch + decode every sample the moment
// the AudioContext unlocks, so the very first tap is already instant.
// ---------------------------------------------------------------------------
const _sampleBuffers = new Map(); // path → AudioBuffer | Promise<AudioBuffer> | "failed"

function _publicPath(path) {
  const base =
    typeof process !== "undefined" && process.env && process.env.PUBLIC_URL
      ? process.env.PUBLIC_URL
      : "";
  return `${base}${path}`;
}

function _decodeSample(ctx, path) {
  const existing = _sampleBuffers.get(path);
  if (existing && existing !== "failed") return existing; // AudioBuffer or in-flight promise
  if (existing === "failed") return null;
  const p = fetch(_publicPath(path))
    .then((r) => {
      if (!r.ok) throw new Error(`fetch ${path} failed`);
      return r.arrayBuffer();
    })
    .then((buf) => new Promise((res, rej) => {
      // Safari accepts both promise-form and callback-form decodeAudioData.
      // Callback form is more reliable on older iOS versions.
      try {
        const maybePromise = ctx.decodeAudioData(buf, res, rej);
        if (maybePromise && typeof maybePromise.then === "function") {
          maybePromise.then(res, rej);
        }
      } catch (e) { rej(e); }
    }))
    .then((audioBuf) => {
      _sampleBuffers.set(path, audioBuf);
      return audioBuf;
    })
    .catch(() => {
      _sampleBuffers.set(path, "failed");
      return null;
    });
  _sampleBuffers.set(path, p);
  return p;
}

// Paths that must be warm the instant the LockScreen mounts.
const _PRELOAD_PATHS = [
  "/audio/iphone_lock.mp3",
  "/audio/iphone_keypad.mp3",
  "/audio/apoc_whatsapp.mp3",
  "/audio/apoc_calendar.mp3",
  "/audio/apoc_messages.mp3",
  "/audio/apoc_mail.mp3",
  "/audio/apoc_interference.mp3",
];

function _preloadAllSamples(ctx) {
  _PRELOAD_PATHS.forEach((p) => _decodeSample(ctx, p));
}

// Kick off preload the moment this module is imported (which happens at
// LockScreen mount, well before the first user tap). iOS Safari allows
// `new AudioContext()` in suspended state without a user gesture, and
// both `fetch()` and `decodeAudioData()` work on a suspended context —
// only `.start()` needs the gesture. This means by the time the user
// swipes up + reads the passcode prompt + taps a digit, every MP3 is
// already fully decoded and sitting in memory, ready to fire at zero
// latency.
export function preloadApocalypseSamples() {
  if (typeof window === "undefined") return;
  const ctx = ensureContext();
  if (!ctx) return;
  _preloadAllSamples(ctx);
}

// Fire the preload eagerly on module import. Wrapped in a try/catch so
// even weird SSR / non-browser contexts don't blow up.
try { preloadApocalypseSamples(); } catch { /* ignore */ }

// Fire a decoded buffer NOW. Zero-latency path — used by every keypad tap
// and every apocalypse banner. Falls back to null if the sample isn't
// decoded yet, which lets the caller run the HTML5 <audio> path instead.
function _playBuffer(path, volume) {
  const ctx = ensureContext();
  if (!ctx) return false;
  const buf = _sampleBuffers.get(path);
  // Only fire if the AudioBuffer is fully decoded and cached in memory.
  if (!buf || typeof buf.duration !== "number") return false;
  try {
    if (ctx.state === "suspended" && typeof ctx.resume === "function") {
      try { ctx.resume(); } catch { /* ignore */ }
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const g = ctx.createGain();
    g.gain.value = Math.max(0, Math.min(1, volume));
    src.connect(g);
    g.connect(ctx.destination);
    src.start(0);
    return true;
  } catch {
    return false;
  }
}

/**
 * Must be called from inside a user gesture (touchstart / pointerdown /
 * click). After unlock the AudioContext stays alive until the page unloads.
 * ALSO kicks off the fetch/decode of every audio sample so the very first
 * tap after unlock is already at zero latency.
 */
export function unlockApocalypseAudio() {
  const ctx = ensureContext();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended" && typeof ctx.resume === "function") {
      ctx.resume();
    }
    if (!unlocked) {
      const buf = ctx.createBuffer(1, 1, 22050);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      src.start(0);
      unlocked = true;
      // Fire-and-forget: decode every MP3 in the background. First tap
      // typically comes ~500 ms later (user reads passcode dots first),
      // by which time all six files are decoded and warm in memory.
      _preloadAllSamples(ctx);
    }
  } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Looped background "phone interference" bed. Plays underneath the entire
// Apocalypse crash sequence to sell the "phone is dying" fantasy — cellular
// static, 3G/4G glitch chatter, screen-tear buzz. Stops the instant the
// error/haunt beat lands so the reveal itself is dead silent for maximum
// contrast.
//
// Implementation: uses the SAME Web Audio infrastructure as the banner
// notification pings (see `_playBuffer` above), which we know works
// perfectly on iOS Safari because the banner sounds have been confirmed
// audible by the magician. AudioBufferSourceNode with loop = true gives
// us seamless, gap-free looping.
//
// Dual redundancy: on the off-chance the AudioContext is unavailable
// (very old iOS, corporate iframe sandbox, etc.), we fall back to a
// persistent HTML5 <audio loop> element that gets created and .load()'d
// at module import time. The Web Audio path is tried first every call.
//
// Idempotent: calling play again while already looping is a no-op.
// Short ramp-out on stop (60 ms) so the cut doesn't produce a click.
// ---------------------------------------------------------------------------
let _interferenceNode = null;
let _interferenceGain = null;
let _interferenceEl = null; // HTML5 fallback, eagerly loaded

function _ensureInterferenceEl() {
  if (typeof window === "undefined") return null;
  if (_interferenceEl) return _interferenceEl;
  try {
    const el = new Audio(_publicPath("/audio/apoc_interference.mp3"));
    el.loop = true;
    el.preload = "auto";
    el.setAttribute("playsinline", "");
    try { el.load(); } catch { /* ignore */ }
    _interferenceEl = el;
    return el;
  } catch {
    return null;
  }
}
// Fire the eager load on module import so the fallback is always ready.
try { _ensureInterferenceEl(); } catch { /* ignore */ }

function _startInterferenceWebAudio(volume) {
  const ctx = ensureContext();
  if (!ctx) return false;
  const buf = _sampleBuffers.get("/audio/apoc_interference.mp3");
  // Only fire if the AudioBuffer is fully decoded (same guard as banners).
  if (!buf || typeof buf.duration !== "number") return false;
  try {
    if (ctx.state === "suspended" && typeof ctx.resume === "function") {
      try { ctx.resume(); } catch { /* ignore */ }
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const g = ctx.createGain();
    g.gain.value = Math.max(0, Math.min(1, volume));
    src.connect(g);
    g.connect(ctx.destination);
    src.start(0);
    _interferenceNode = src;
    _interferenceGain = g;
    return true;
  } catch {
    return false;
  }
}

function _startInterferenceHtml(volume) {
  const el = _ensureInterferenceEl();
  if (!el) return false;
  try {
    if (el.paused || el.ended) {
      try { el.currentTime = 0; } catch { /* ignore */ }
    }
    el.volume = Math.max(0, Math.min(1, volume));
    const p = el.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => {
        // If play() rejected (no gesture context yet), retry once on
        // next tick — often the banner pings fire immediately after
        // and their play() bootstraps the audio session.
        try {
          setTimeout(() => {
            try { el.play(); } catch { /* ignore */ }
          }, 40);
        } catch { /* ignore */ }
      });
    }
    return true;
  } catch {
    return false;
  }
}

export function playInterferenceLoop({ volume = 0.55 } = {}) {
  if (_interferenceNode || (_interferenceEl && !_interferenceEl.paused)) return;
  const v = Math.max(0, Math.min(1, volume));
  if (v === 0) return;
  // Try Web Audio first — same infrastructure as the banner pings that
  // are already confirmed working.
  if (_startInterferenceWebAudio(v)) return;
  // Buffer wasn't decoded in time — kick off decode for future runs
  // AND start the HTML5 fallback so the spectator gets static NOW.
  try {
    const ctx = ensureContext();
    if (ctx) _decodeSample(ctx, "/audio/apoc_interference.mp3");
  } catch { /* ignore */ }
  _startInterferenceHtml(v);
}

export function stopInterferenceLoop({ fadeMs = 80 } = {}) {
  // ---- Web Audio path ----
  const src = _interferenceNode;
  const g = _interferenceGain;
  _interferenceNode = null;
  _interferenceGain = null;
  if (src) {
    try {
      const ctx = ensureContext();
      if (ctx && g) {
        const now = ctx.currentTime;
        const end = now + Math.max(0.005, fadeMs / 1000);
        try {
          g.gain.cancelScheduledValues(now);
          g.gain.setValueAtTime(g.gain.value, now);
          g.gain.linearRampToValueAtTime(0.0001, end);
        } catch { /* ignore */ }
        try { src.stop(end + 0.01); } catch { /* ignore */ }
      } else {
        try { src.stop(0); } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
  }
  // ---- HTML5 fallback path ----
  const el = _interferenceEl;
  if (el && !el.paused) {
    try {
      const steps = 6;
      const startVol = el.volume;
      const stepMs = Math.max(6, Math.floor(fadeMs / steps));
      let i = 0;
      const rampId = setInterval(() => {
        i += 1;
        try { el.volume = Math.max(0, startVol * (1 - i / steps)); } catch { /* ignore */ }
        if (i >= steps) {
          clearInterval(rampId);
          try { el.pause(); } catch { /* ignore */ }
          try { el.currentTime = 0; } catch { /* ignore */ }
          try { el.volume = startVol; } catch { /* ignore */ }
        }
      }, stepMs);
    } catch { /* ignore */ }
  }
}

// Small helper — resume the context if the OS suspended it between plays.
function primeContext() {
  const ctx = ensureContext();
  if (!ctx) return null;
  if (ctx.state === "suspended" && typeof ctx.resume === "function") {
    try { ctx.resume(); } catch { /* ignore */ }
  }
  return ctx;
}

// A shared low-pass "room" tail: creates a subtle reverb-like send by
// routing a portion of each note through a delay + feedback loop. Built
// once per AudioContext, connected to destination. This is what makes the
// synthesised sounds feel "played through a phone speaker" rather than
// "raw oscillator" — the tiny reflections our ear expects from real
// notifications finally show up.
let reverbSendCache = null;
function getReverbSend(ctx) {
  if (reverbSendCache && reverbSendCache.ctx === ctx) return reverbSendCache;
  const input = ctx.createGain();
  input.gain.value = 1;
  const delayA = ctx.createDelay(0.4);
  delayA.delayTime.value = 0.045;
  const delayB = ctx.createDelay(0.4);
  delayB.delayTime.value = 0.083;
  const feedback = ctx.createGain();
  feedback.gain.value = 0.22;
  const highcut = ctx.createBiquadFilter();
  highcut.type = "lowpass";
  highcut.frequency.value = 5200;
  const wet = ctx.createGain();
  wet.gain.value = 0.35;
  input.connect(delayA);
  delayA.connect(highcut);
  highcut.connect(delayB);
  delayB.connect(feedback);
  feedback.connect(delayA);
  highcut.connect(wet);
  wet.connect(ctx.destination);
  reverbSendCache = { ctx, input };
  return reverbSendCache;
}

// Build one voice: an oscillator through a gain envelope. Returns { osc, gain }.
// Optional `sendReverb` routes a copy through the shared reverb send.
function voice(ctx, type, freq, t0, attack, hold, release, peakGain, sendReverb = false) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  const tAtk = t0 + attack;
  const tHold = tAtk + hold;
  const tEnd = tHold + release;
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(peakGain, tAtk);
  gain.gain.linearRampToValueAtTime(peakGain, tHold);
  // Exponential-feeling tail via a small floor value → linear ramp works
  // on all iOS Safari versions where exp ramps to 0 throw.
  gain.gain.linearRampToValueAtTime(0.0001, tEnd);
  osc.connect(gain);
  gain.connect(ctx.destination);
  if (sendReverb) {
    try {
      const rev = getReverbSend(ctx);
      const send = ctx.createGain();
      send.gain.value = 0.55;
      gain.connect(send);
      send.connect(rev.input);
    } catch { /* reverb optional — ignore setup failures */ }
  }
  osc.start(t0);
  osc.stop(tEnd + 0.02);
  return { osc, gain, tEnd };
}

// ---------------------------------------------------------------------------
// Legacy tri-tone (kept as the generic fallback + used by row-spawn code)
// E5 → A5 → E6 sine cascade, ≈ 350 ms total.
// ---------------------------------------------------------------------------
export function playNotificationPing({ volume = 0.7 } = {}) {
  const ctx = primeContext();
  if (!ctx) return;
  const v = Math.max(0, Math.min(1, volume));
  if (v === 0) return;
  const now = ctx.currentTime;
  [
    { freq: 659.25, start: 0.00, dur: 0.34 },
    { freq: 880.00, start: 0.07, dur: 0.32 },
    { freq: 1318.5, start: 0.14, dur: 0.32 },
  ].forEach(({ freq, start, dur }) => {
    voice(ctx, "sine", freq, now + start, 0.012, 0, dur, 0.32 * v, true);
  });
}

// ---------------------------------------------------------------------------
// WhatsApp — signature two-note "bloop bloop". Real WhatsApp Note sound
// analysed: two ascending notes with a soft bubble-like attack. The
// characteristic wetness comes from a subtle reverb tail (implemented via
// the shared reverb send). Frequencies chosen to match the actual WhatsApp
// notification: G5→D6 (approx) with slight detune between voices for
// chorus / phone-speaker realism.
// ---------------------------------------------------------------------------
export function playWhatsAppPing({ volume = 0.7 } = {}) {
  const ctx = primeContext();
  if (!ctx) return;
  const v = Math.max(0, Math.min(1, volume));
  if (v === 0) return;
  const now = ctx.currentTime;
  // Bloop 1 — G5 (~784 Hz) with slight upward pitch tail = "bloop" quality
  voice(ctx, "sine", 783.99, now + 0.00, 0.008, 0.045, 0.14, 0.32 * v, true);
  voice(ctx, "sine", 786.5,  now + 0.00, 0.008, 0.045, 0.14, 0.10 * v, false); // detune
  voice(ctx, "sine", 1568.0, now + 0.00, 0.008, 0.030, 0.09, 0.05 * v, false); // 2× shimmer
  // Bloop 2 — D6 (1174 Hz), 100 ms later, matching WhatsApp's cadence
  voice(ctx, "sine", 1174.66, now + 0.100, 0.008, 0.055, 0.16, 0.30 * v, true);
  voice(ctx, "sine", 1178.3,  now + 0.100, 0.008, 0.055, 0.16, 0.09 * v, false);
  voice(ctx, "sine", 2349.3,  now + 0.100, 0.008, 0.035, 0.10, 0.05 * v, false);
}

// ---------------------------------------------------------------------------
// Calendar / Reminders — cascading three-note trill matching the real iOS
// Calendar/Reminder alert. Each bell has a mallet tick + harmonic body +
// reverb send. The ascending pattern (E6 → G6 → C7) gives the "Wake up you
// have a meeting" signature everyone recognises.
// ---------------------------------------------------------------------------
export function playCalendarChime({ volume = 0.7 } = {}) {
  const ctx = primeContext();
  if (!ctx) return;
  const v = Math.max(0, Math.min(1, volume));
  if (v === 0) return;
  const now = ctx.currentTime;
  const notes = [
    { freq: 1318.5, at: 0.00, gain: 0.28 }, // E6
    { freq: 1567.98, at: 0.14, gain: 0.28 }, // G6
    { freq: 2093.0, at: 0.28, gain: 0.30 }, // C7
  ];
  notes.forEach(({ freq, at, gain }) => {
    // Fundamental bell body with reverb tail
    voice(ctx, "sine",     freq,     now + at, 0.003, 0.02, 0.55, gain * v, true);
    // 2× harmonic — bell brightness
    voice(ctx, "sine",     freq * 2, now + at, 0.003, 0.02, 0.30, gain * 0.35 * v, false);
    // Mallet tick — extremely brief square strike
    voice(ctx, "square",   freq * 3, now + at, 0.001, 0.003, 0.010, gain * 0.15 * v, false);
  });
}

// ---------------------------------------------------------------------------
// Messages / iMessage — classic single "note" ding. Bright bell-like G6
// with a reverb send for phone-speaker realism.
// ---------------------------------------------------------------------------
export function playMessagesTone({ volume = 0.7 } = {}) {
  const ctx = primeContext();
  if (!ctx) return;
  const v = Math.max(0, Math.min(1, volume));
  if (v === 0) return;
  const now = ctx.currentTime;
  voice(ctx, "sine",  1568.0, now, 0.003, 0.02, 0.42, 0.34 * v, true);   // G6
  voice(ctx, "sine",  1573.0, now, 0.003, 0.02, 0.42, 0.10 * v, false);  // detune
  voice(ctx, "sine",  3136.0, now, 0.003, 0.02, 0.22, 0.10 * v, false);  // 2× harmonic
  // Mallet transient
  voice(ctx, "triangle", 4700, now, 0.001, 0.003, 0.010, 0.06 * v, false);
}

// ---------------------------------------------------------------------------
// Mail — soft two-note bell with reverb. Real iOS "New Mail" is short and
// mellow, doesn't shout for attention. E5 + B5 stacked, quick decay.
// ---------------------------------------------------------------------------
export function playMailTone({ volume = 0.7 } = {}) {
  const ctx = primeContext();
  if (!ctx) return;
  const v = Math.max(0, Math.min(1, volume));
  if (v === 0) return;
  const now = ctx.currentTime;
  voice(ctx, "sine", 659.25, now,        0.006, 0.03, 0.55, 0.28 * v, true);  // E5
  voice(ctx, "sine", 987.77, now + 0.04, 0.006, 0.03, 0.52, 0.26 * v, true);  // B5
  voice(ctx, "sine", 1974.5, now + 0.04, 0.006, 0.02, 0.28, 0.08 * v, false); // shimmer
}

// ---------------------------------------------------------------------------
// Settings — neutral system ding. Slight reverb.
// ---------------------------------------------------------------------------
export function playSettingsDing({ volume = 0.6 } = {}) {
  const ctx = primeContext();
  if (!ctx) return;
  const v = Math.max(0, Math.min(1, volume));
  if (v === 0) return;
  const now = ctx.currentTime;
  voice(ctx, "sine", 1046.5, now, 0.005, 0.03, 0.50, 0.26 * v, true);
  voice(ctx, "sine",  523.25, now, 0.005, 0.03, 0.45, 0.10 * v, false);
}

// ---------------------------------------------------------------------------
// App Store — upward chime (C6 → E6) with reverb tail.
// ---------------------------------------------------------------------------
export function playAppStoreChime({ volume = 0.6 } = {}) {
  const ctx = primeContext();
  if (!ctx) return;
  const v = Math.max(0, Math.min(1, volume));
  if (v === 0) return;
  const now = ctx.currentTime;
  voice(ctx, "sine", 1046.5, now + 0.00, 0.005, 0.04, 0.30, 0.26 * v, true);
  voice(ctx, "sine", 1318.5, now + 0.10, 0.005, 0.04, 0.32, 0.26 * v, true);
}

// ---------------------------------------------------------------------------
// Find My — locator beep. Two triangle blips at ~2 kHz. Slight reverb.
// ---------------------------------------------------------------------------
export function playFindMyBeep({ volume = 0.6 } = {}) {
  const ctx = primeContext();
  if (!ctx) return;
  const v = Math.max(0, Math.min(1, volume));
  if (v === 0) return;
  const now = ctx.currentTime;
  voice(ctx, "triangle", 1975.5, now + 0.00, 0.003, 0.03, 0.08, 0.28 * v, true);
  voice(ctx, "triangle", 1975.5, now + 0.14, 0.003, 0.03, 0.08, 0.24 * v, true);
}

// ---------------------------------------------------------------------------
// Photos — muted bell A5 with reverb tail.
// ---------------------------------------------------------------------------
export function playPhotosTone({ volume = 0.6 } = {}) {
  const ctx = primeContext();
  if (!ctx) return;
  const v = Math.max(0, Math.min(1, volume));
  if (v === 0) return;
  const now = ctx.currentTime;
  voice(ctx, "sine",     880.0, now, 0.005, 0.02, 0.48, 0.24 * v, true);
  voice(ctx, "triangle", 1760.0, now, 0.004, 0.02, 0.24, 0.08 * v, false);
}

// ---------------------------------------------------------------------------
// Real-sample notification playback — some app tones are impossible to fake
// with pure synthesis (their timbre is a designed sound the user recognises
// instantly). When the magician records a genuine sample and we ship it as
// a static asset, this helper plays it via a lazy-cached <audio> template
// with `cloneNode()` per fire, so back-to-back Apocalypse banners never
// clip each other mid-playback. Returns `true` if the sample fired,
// `false` if the file failed to load (caller can then run its synth
// fallback). The `false` path also runs the fallback synchronously once
// per lifetime, since <audio> element errors are usually permanent (404 /
// codec) and re-trying on every tap would spam the console.
// ---------------------------------------------------------------------------
const _sampleTemplates = new Map(); // path → { el, failed }
function _getSampleTemplate(path) {
  if (_sampleTemplates.has(path)) return _sampleTemplates.get(path);
  const base = typeof process !== "undefined" && process.env && process.env.PUBLIC_URL
    ? process.env.PUBLIC_URL
    : "";
  const el = new Audio(`${base}${path}`);
  el.preload = "auto";
  const entry = { el, failed: false };
  el.addEventListener("error", () => { entry.failed = true; }, { once: true });
  _sampleTemplates.set(path, entry);
  return entry;
}
function _playSample(path, volume) {
  const v = Math.max(0, Math.min(1, volume));
  if (v === 0) return true;
  // Zero-latency path: use the pre-decoded AudioBuffer if it's ready.
  if (_playBuffer(path, v)) return true;
  // Buffer not ready yet — kick off decode so subsequent plays are fast,
  // and fall back to the <audio> element for this one call.
  try {
    const ctx = ensureContext();
    if (ctx) _decodeSample(ctx, path);
  } catch { /* ignore */ }
  try {
    const entry = _getSampleTemplate(path);
    if (entry.failed) return false;
    const clone = entry.el.cloneNode(true);
    clone.volume = v;
    const p = clone.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
    clone.addEventListener("ended", () => {
      try { clone.src = ""; } catch { /* ignore */ }
    }, { once: true });
    return true;
  } catch {
    return false;
  }
}

// Per-app sample paths. Every known app is mapped to ONE of the four
// authentic MP3 samples the user recorded, so the Apocalypse cacophony is
// built entirely from real iOS notification sounds — no synth tells.
// Categorisation logic:
//   • messages   → chat / DM apps (single "note" ding)
//   • whatsapp   → other social / messenger (two-bloop)
//   • calendar   → alerts / reminders / system (bell trill)
//   • mail       → email / media / misc (soft bell)
const APP_SAMPLE_PATHS = {
  // Chat / DM (messages ding)
  messages: "/audio/apoc_messages.mp3",
  imessage: "/audio/apoc_messages.mp3",
  signal: "/audio/apoc_messages.mp3",
  telegram: "/audio/apoc_messages.mp3",
  discord: "/audio/apoc_messages.mp3",
  slack: "/audio/apoc_messages.mp3",
  teams: "/audio/apoc_messages.mp3",
  // Social / messenger (whatsapp bloop)
  whatsapp: "/audio/apoc_whatsapp.mp3",
  messenger: "/audio/apoc_whatsapp.mp3",
  snapchat: "/audio/apoc_whatsapp.mp3",
  instagram: "/audio/apoc_whatsapp.mp3",
  facebook: "/audio/apoc_whatsapp.mp3",
  tiktok: "/audio/apoc_whatsapp.mp3",
  x: "/audio/apoc_whatsapp.mp3",
  twitter: "/audio/apoc_whatsapp.mp3",
  // Alerts / reminders / system (calendar chime)
  calendar: "/audio/apoc_calendar.mp3",
  reminders: "/audio/apoc_calendar.mp3",
  clock: "/audio/apoc_calendar.mp3",
  alarm: "/audio/apoc_calendar.mp3",
  alarms: "/audio/apoc_calendar.mp3",
  settings: "/audio/apoc_calendar.mp3",
  "software update": "/audio/apoc_calendar.mp3",
  "find my": "/audio/apoc_calendar.mp3",
  "find my iphone": "/audio/apoc_calendar.mp3",
  maps: "/audio/apoc_calendar.mp3",
  weather: "/audio/apoc_calendar.mp3",
  health: "/audio/apoc_calendar.mp3",
  wallet: "/audio/apoc_calendar.mp3",
  home: "/audio/apoc_calendar.mp3",
  fitness: "/audio/apoc_calendar.mp3",
  // Email / media / misc (mail bell)
  mail: "/audio/apoc_mail.mp3",
  gmail: "/audio/apoc_mail.mp3",
  outlook: "/audio/apoc_mail.mp3",
  photos: "/audio/apoc_mail.mp3",
  camera: "/audio/apoc_mail.mp3",
  notes: "/audio/apoc_mail.mp3",
  "app store": "/audio/apoc_mail.mp3",
  files: "/audio/apoc_mail.mp3",
  music: "/audio/apoc_mail.mp3",
  podcasts: "/audio/apoc_mail.mp3",
  spotify: "/audio/apoc_mail.mp3",
  youtube: "/audio/apoc_mail.mp3",
  news: "/audio/apoc_mail.mp3",
  safari: "/audio/apoc_mail.mp3",
  chrome: "/audio/apoc_mail.mp3",
};

// Fallback rotation for apps that aren't in the mapping (user-added custom
// entries, misspellings, etc.). Cycles through all four real samples so a
// burst of unknown-app banners doesn't sound identical each time — matches
// the natural variation of a real phone under an alert storm.
const _FALLBACK_SAMPLES = [
  "/audio/apoc_messages.mp3",
  "/audio/apoc_whatsapp.mp3",
  "/audio/apoc_calendar.mp3",
  "/audio/apoc_mail.mp3",
];
let _fallbackCursor = 0;
function _nextFallbackSample(name) {
  // Derive a stable-ish index from the app name so the SAME custom app
  // always plays the SAME sound within a session (predictable rehearsal),
  // but different custom apps use different sounds (variety in a storm).
  if (name && name.length) {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
    return _FALLBACK_SAMPLES[Math.abs(h) % _FALLBACK_SAMPLES.length];
  }
  // Truly anonymous — rotate for variety.
  const p = _FALLBACK_SAMPLES[_fallbackCursor % _FALLBACK_SAMPLES.length];
  _fallbackCursor++;
  return p;
}

// ---------------------------------------------------------------------------
// Dispatcher — map app label → real MP3 sample. Every app now plays one of
// the four authentic iOS notification sounds the user recorded. Unknown /
// custom apps get a deterministic hash-based sample so the same custom
// name always sounds the same, but different names spread across the four
// real samples — no synthesised tells anywhere in the trick.
// ---------------------------------------------------------------------------
export function playAppNotification(app, { volume = 0.7 } = {}) {
  const name = String(app || "").trim().toLowerCase();
  const samplePath = APP_SAMPLE_PATHS[name] || _nextFallbackSample(name);
  _playSample(samplePath, volume);
}

// ---------------------------------------------------------------------------
// iPhone keypad tap — plays the user-provided .mp3 sample when a digit is
// tapped on the fake lockscreen keypad. Prefers the pre-decoded Web Audio
// buffer path (zero-latency, sample-accurate) and falls back to <audio>
// cloneNode() only if the buffer isn't ready yet. Real iOS taps overlap
// slightly when the user types fast — both paths support that natively.
// ---------------------------------------------------------------------------
let _keypadTemplateEl = null;
export function playIphoneKeypadTap({ volume = 0.6 } = {}) {
  const v = Math.max(0, Math.min(1, volume));
  if (v === 0) return;
  // Zero-latency path — decoded once, fired sample-accurate every tap.
  if (_playBuffer("/audio/iphone_keypad.mp3", v)) return;
  // Ensure decode has been kicked off so the next tap can use fast path.
  try {
    const ctx = ensureContext();
    if (ctx) _decodeSample(ctx, "/audio/iphone_keypad.mp3");
  } catch { /* ignore */ }
  try {
    if (!_keypadTemplateEl) {
      _keypadTemplateEl = new Audio(_publicPath("/audio/iphone_keypad.mp3"));
      _keypadTemplateEl.preload = "auto";
    }
    const el = _keypadTemplateEl.cloneNode(true);
    el.volume = v;
    const p = el.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
    el.addEventListener("ended", () => {
      try { el.src = ""; } catch { /* ignore */ }
    }, { once: true });
  } catch {
    /* ignore — silent fallback keeps the trick uninterrupted */
  }
}

// ---------------------------------------------------------------------------
// iPhone lock click — plays the REAL sampled sound provided by the user.
// Prefers the pre-decoded Web Audio buffer (zero-latency) and falls back
// to <audio> only if decode hasn't finished yet.
// ---------------------------------------------------------------------------
let _lockAudioEl = null;
export function playIphoneLockClick({ volume = 0.9 } = {}) {
  const v = Math.max(0, Math.min(1, volume));
  if (v === 0) return;
  if (_playBuffer("/audio/iphone_lock.mp3", v)) return;
  try {
    const ctx = ensureContext();
    if (ctx) _decodeSample(ctx, "/audio/iphone_lock.mp3");
  } catch { /* ignore */ }
  try {
    if (!_lockAudioEl) {
      _lockAudioEl = new Audio(_publicPath("/audio/iphone_lock.mp3"));
      _lockAudioEl.preload = "auto";
    }
    _lockAudioEl.currentTime = 0;
    _lockAudioEl.volume = v;
    const p = _lockAudioEl.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  } catch {
    /* ignore — falling back to silence is preferable to breaking the flow */
  }
}

// ---------------------------------------------------------------------------
// Post-reveal "error" sound. Historically a synthesised iOS tri-tone
// (G#4 → E4 squares), but the magician requested a specific "UI error pop"
// sample so we now stream the real MP3 shipped in /public/audio/. Falls
// back to silence on decode failure (never blocks the flow).
// ---------------------------------------------------------------------------
let _errorAudioEl = null;
export function playErrorTritone({ volume = 0.8 } = {}) {
  try {
    const v = Math.max(0, Math.min(1, volume));
    if (v === 0) return;
    if (!_errorAudioEl) {
      _errorAudioEl = new Audio(_publicPath("/audio/apoc_error.mp3"));
      _errorAudioEl.preload = "auto";
    }
    _errorAudioEl.currentTime = 0;
    _errorAudioEl.volume = v;
    const p = _errorAudioEl.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  } catch {
    /* ignore — silence is better than a broken reveal */
  }
}
